import { Storybook } from "@tento-ui/ui/storybook";
import { DshTurnTool } from "./tool";

/** The fixture's wide output, verbatim from `thread/fixture.ts` — nine lines
 * with one far past any column, which is what finds a body that scrolls the
 * page instead of itself. */
const WIDE_OUTPUT = [
  "   Compiling tento-pane-runtime v0.1.0 (/Users/james/workspace/tento/tento-pane-runtime)",
  "warning: unused variable: `lane_id` --> tento-pane-runtime/src/dispatch.rs:412:23",
  "    Finished `test` profile [unoptimized + debuginfo] target(s) in 41.62s",
  "     Running unittests src/lib.rs (/Users/james/ssd-mount/cargo-target/debug/deps/tento_pane_runtime-9f2c1ab4e7d60358)",
  "running 212 tests",
  "test dispatch::tests::a_dropped_lane_settles_its_open_turn ... ok",
  "test dispatch::tests::records_interleave_by_clock_not_by_seq ... ok",
  "test queue::tests::a_queued_unit_survives_a_restart ... ok",
  "test result: ok. 212 passed; 0 failed; 0 ignored; 0 measured; 0 filtered out; finished in 2.41s",
].join("\n");

/** A failure, which is the case that renders BOTH sections of the card: the
 * row's subject has been replaced by the first line of this, so the command
 * has nowhere else to be and becomes `IN`. */
const BUILD_ERROR = [
  "error: The symbol \"bindPrimaryCell\" has already been declared",
  "    at routes/sidebar.ts:328:10",
  "    at routes/sidebar.ts:411:10",
  "",
  "  The build failed after 0.9s.",
].join("\n");

/** A real two-file patch — git headers, three hunks, and content that is
 * actually a change this tree shipped.
 *
 * Longer than the 8-line cap on purpose. A patch is the body most likely to
 * exceed it in a real transcript, and the three things this card is for —
 * signs drawn in a gutter the selection cannot see, hunk headers drawn as
 * GAPS rather than as content, and a footer that totals the WHOLE patch —
 * are exactly the three that have to survive the fold. Open it and the
 * totals do not move. */
const PATCH = [
  "diff --git a/tento-tui/tento-tui-scene/src/build.rs b/tento-tui/tento-tui-scene/src/build.rs",
  "index 1e2533ea..03c5117d 100644",
  "--- a/tento-tui/tento-tui-scene/src/build.rs",
  "+++ b/tento-tui/tento-tui-scene/src/build.rs",
  "@@ -1313,9 +1313,9 @@ impl SceneBuilder {",
  "         let literal = args",
  "             .get(i + 1)",
  "             .and_then(SceneExpr::as_str)",
  "-        return parse_rgb_hex(literal)",
  "+        return parse_hex_rgb(literal)",
  "             .map(Some)",
  "-            .ok_or_else(|| anyhow!(\"{renderer}: :{keyword} wants #rrggbb\"));",
  "+            .map_err(|_| anyhow!(\"{renderer}: :{keyword} wants #rrggbb\"));",
  "     }",
  "@@ -1476,7 +1476,9 @@ impl SceneBuilder {",
  " fn parse_hex_rgb(literal: &str) -> Result<Rgb, HexError> {",
  "-    let body = literal.strip_prefix('#').unwrap_or(literal);",
  "+    let body = literal.strip_prefix('#').ok_or(HexError::NoHash)?;",
  "+    if body.len() != 6 {",
  "+        return Err(HexError::Width(body.len()));",
  "+    }",
  "     u32::from_str_radix(body, 16).map(Rgb::from_u32).map_err(HexError::Radix)",
  " }",
  "diff --git a/tento-ui-js/components/turn-dsh/tool.ts b/tento-ui-js/components/turn-dsh/tool.ts",
  "index 8ac41f0b..b2d0e614 100644",
  "--- a/tento-ui-js/components/turn-dsh/tool.ts",
  "+++ b/tento-ui-js/components/turn-dsh/tool.ts",
  "@@ -566,9 +566,8 @@ export class DshTurnTool extends LonaWebComponent {",
  "   private static diffRow(row: DshTurnTool.DiffLine): HTMLElement {",
  "     const $row = document.createElement(\"div\");",
  "-    $row.className = \"diff-line\";",
  "-    $row.style.color = DshTurnTool.diffColor(row.text);",
  "     $row.textContent = row.text;",
  "+    $row.classList.add(\"diff-line\", \"diff-\" + row.kind);",
  "     return $row;",
  "   }",
].join("\n");

/** The fixture's own patch — short enough to sit under the cap, so the card
 * is the whole patch and the footer totals what is on screen. */
const SHORT_DIFF = [
  "@@ -60,7 +60,21 @@",
  "-  return lanes.flatMap((lane) => lane.records).sort((a, b) => a.seq - b.seq);",
  "+  const cursors = lanesFor(instance, laneId).map((lane) => ({",
  "+    lane,",
  "+    clock: laneClock(lane),",
  "+    index: 0,",
  "+  }));",
  "   // the interleave reads the clock; the lane keeps its own order",
].join("\n");

export class DshTurnToolStoryBook extends Storybook.Default {
  title(): string {
    return "Turn · dsh · tool";
  }

  examples(): Storybook.Example[] {
    return [
      {
        title: "Every status, in the order a call moves through them",
        render: () => {
          const $wrap = column();
          $wrap.append(
            DshTurnTool.makeWith({
              tool: "read",
              verb: "Read",
              title: "tento-pane-runtime/src/dispatch.rs",
              note: "412 lines",
              status: "pending",
            }),
            DshTurnTool.makeWith({
              tool: "execute",
              verb: "Run",
              title: "cargo build -p tento-spool-server",
              status: "running",
            }),
            DshTurnTool.makeWith({
              tool: "search",
              verb: "Search",
              title: "seq",
              note: "31 matches in 12 files",
              status: "done",
            }),
            DshTurnTool.makeWith({
              tool: "edit",
              verb: "Update",
              title: "html/routes/stream-render.ts",
              note: "+12 −4",
              status: "done",
            }),
            DshTurnTool.makeWith({
              tool: "execute",
              verb: "Run",
              title: "rm -rf ~/.tento-sh/sessions",
              note: "not run",
              status: "declined",
            }),
          );
          return $wrap;
        },
      },
      {
        title: "The output card — closed, then open",
        render: () => {
          const $wrap = column();
          $wrap.append(
            DshTurnTool.makeWith({
              tool: "execute",
              verb: "Run",
              title: "cargo test -q -p tento-pane-runtime",
              note: "exit 0 · 41.6s",
              status: "done",
              detail: { text: WIDE_OUTPUT, language: "plain" },
            }),
            DshTurnTool.makeWith({
              tool: "execute",
              verb: "Run",
              title: "cargo test -q -p tento-pane-runtime",
              note: "exit 0 · 41.6s",
              status: "done",
              detail: { text: WIDE_OUTPUT, language: "plain" },
              expanded: true,
            }),
          );
          return $wrap;
        },
      },
      {
        title: "A failure puts the error on the row and the command in IN",
        render: () =>
          DshTurnTool.makeWith({
            tool: "execute",
            verb: "Run",
            title: "deno run -A build.deno.ts",
            note: "exit 1 · 0.9s",
            status: "failed",
            detail: { text: BUILD_ERROR, language: "plain" },
            expanded: true,
          }),
      },
      {
        title: "A patch under the cap — signs in a gutter, totals underneath",
        render: () =>
          DshTurnTool.makeWith({
            tool: "edit",
            verb: "Update",
            title: "html/routes/stream-items.ts",
            note: "+38 −11",
            status: "done",
            detail: { text: SHORT_DIFF, language: "diff" },
            expanded: true,
          }),
      },
      {
        title: "A two-file patch, capped at four lines a side",
        render: () =>
          DshTurnTool.makeWith({
            tool: "edit",
            verb: "Update",
            title: "tento-tui-scene/src/build.rs",
            note: "+7 −5",
            status: "done",
            detail: { text: PATCH, language: "diff" },
            expanded: true,
          }),
      },
      {
        title: "A batched call keeps its constituents outside the fold",
        render: () =>
          DshTurnTool.makeWith({
            tool: "execute",
            verb: "Explore",
            title:
              "/bin/zsh -lc \"rg -n 'fn row_head' tento-tui-panes/src/trace_pane.rs && sed -n '1580,1665p' tento-tui-panes/src/trace_pane.rs\"",
            note: "exit 0 · 1.6s",
            actions: [
              { verb: "Search", subject: "fn row_head|row_step\\( in trace_pane.rs" },
              { verb: "Read", subject: "trace_pane.rs" },
              { verb: "List", subject: "tento-tui-panes/src" },
            ],
            status: "done",
          }),
      },
      {
        title: "One line of output is a trailing fact, not a body",
        render: () =>
          DshTurnTool.makeWith({
            tool: "execute",
            verb: "Run",
            title: "git rev-list --count origin/master..master",
            status: "done",
            detail: { text: "14", language: "plain" },
          }),
      },
      {
        title: "A long subject ellipsises; the trailing fact never does",
        render: () =>
          DshTurnTool.makeWith({
            tool: "read",
            verb: "Read",
            title:
              "/Users/james/workspace/tento/tento-node-registry/tento-spool.node/html/routes/session-detail.ts",
            note: "161 lines",
            status: "done",
          }),
      },
      {
        title: "No verb — a surface with one tool says its name instead",
        render: () =>
          DshTurnTool.makeWith({
            tool: "web_search",
            title: "acp permission option kinds",
            note: "6 sources",
            status: "done",
          }),
      },
    ];
  }
}

function column(): HTMLElement {
  const $wrap = document.createElement("div");
  $wrap.style.display = "flex";
  $wrap.style.flexDirection = "column";
  $wrap.style.gap = "2px";
  $wrap.style.width = "100%";
  return $wrap;
}
